/**
 * Banner 裁剪的纯几何换算：裁剪框固定 2.5:1，图片在裁剪框内可拖动、可缩放，
 * 所有坐标都是「图片左上角相对裁剪框左上角」的偏移（容器像素），导出时反推原图像素区域。
 */

export interface Size {
    width: number;
    height: number;
}

export interface Offset {
    x: number;
    y: number;
}

export interface Rect extends Offset, Size {}

/** 让图片至少铺满裁剪框的最小缩放倍数（相对原图像素） */
export function coverScale(image: Size, box: Size): number {
    if (image.width <= 0 || image.height <= 0) return 1;
    return Math.max(box.width / image.width, box.height / image.height);
}

/** 原图按 scale 缩放后的显示尺寸 */
export function scaledSize(image: Size, scale: number): Size {
    return { width: image.width * scale, height: image.height * scale };
}

/** 把图片居中放进裁剪框时的偏移 */
export function centeredOffset(scaled: Size, box: Size): Offset {
    return { x: (box.width - scaled.width) / 2, y: (box.height - scaled.height) / 2 };
}

/** 偏移限制在「图片始终盖住整个裁剪框」的范围内：x ∈ [box.width - scaled.width, 0] */
export function clampOffset(offset: Offset, scaled: Size, box: Size): Offset {
    const minX = Math.min(box.width - scaled.width, 0);
    const minY = Math.min(box.height - scaled.height, 0);
    return {
        x: Math.min(0, Math.max(minX, offset.x)),
        y: Math.min(0, Math.max(minY, offset.y)),
    };
}

/** 缩放时以裁剪框中心为锚点：中心点对应的原图像素不动 */
export function rescaleOffset(offset: Offset, box: Size, fromScale: number, toScale: number): Offset {
    const centerX = box.width / 2;
    const centerY = box.height / 2;
    const imageX = (centerX - offset.x) / fromScale;
    const imageY = (centerY - offset.y) / fromScale;
    return { x: centerX - imageX * toScale, y: centerY - imageY * toScale };
}

/** 由偏移与缩放反推裁剪框对应的原图像素区域（+0 消掉 -0，便于比较与日志） */
export function sourceRect(offset: Offset, scale: number, box: Size): Rect {
    return {
        x: -offset.x / scale + 0,
        y: -offset.y / scale + 0,
        width: box.width / scale,
        height: box.height / scale,
    };
}

/** 画布导出：把原图的 rect 区域缩放画到 output 尺寸的 png */
export function cropImageToBlob(image: CanvasImageSource, rect: Rect, output: Size): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        canvas.width = output.width;
        canvas.height = output.height;
        const context = canvas.getContext('2d');
        if (!context) {
            reject(new Error('canvas 2d context unavailable'));
            return;
        }
        context.drawImage(image, rect.x, rect.y, rect.width, rect.height, 0, 0, output.width, output.height);
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('canvas toBlob failed'));
        }, 'image/png');
    });
}

/** 裁剪产物沿用原文件名、扩展名改 .png */
export function croppedFileName(originalName: string): string {
    const base = originalName.replace(/\.[^.]+$/, '') || 'banner';
    return `${base}.png`;
}
